import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

import type { CredentialMeta } from "./use-credentials"
import { credentialsListQueryKey } from "./use-credentials"

export interface UpdateCredentialInput {
  credentialId: string
  organizationId: Organization["id"]
  payload: UpdateCredentialPayload
}

/** Fields editable via Pipelines `CredentialUpdate` (plus required body `id`). */
export interface UpdateCredentialPayload {
  notes: string
  password: string
  providerName?: string
  username: string
  website: string
}

export interface UpdateCredentialResponse {
  credentialId?: string
  id?: string
}

const patchCredentialInList = (
  credentials: CredentialMeta[] | undefined,
  credentialId: string,
  patch: Pick<UpdateCredentialPayload, "notes" | "username" | "website">
): CredentialMeta[] | undefined => {
  const credential = credentials?.find(({ id }) => id === credentialId)
  if (!credentials || !credential) {
    return credentials
  }

  if (
    credential.notes === patch.notes &&
    credential.username === patch.username &&
    credential.website === patch.website
  ) {
    return credentials
  }

  return credentials.map((currentCredential) =>
    currentCredential.id === credentialId
      ? {
          ...currentCredential,
          notes: patch.notes,
          username: patch.username,
          website: patch.website,
        }
      : currentCredential
  )
}

export const useUpdateCredential = (
  options?: UseMutationOptions<
    UpdateCredentialResponse,
    HttpError,
    UpdateCredentialInput
  >
) => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    UpdateCredentialResponse,
    HttpError,
    UpdateCredentialInput
  >({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({
      credentialId,
      organizationId,
      payload,
    }: UpdateCredentialInput) => {
      try {
        const response = await httpClient.put<UpdateCredentialResponse>(
          `/api/v1/organizations/${organizationId}/pipelines/credentials/${credentialId}`,
          {
            id: credentialId,
            notes: payload.notes,
            password: payload.password,
            providerName: payload.providerName,
            username: payload.username,
            website: payload.website,
          }
        )

        return response.data
      } catch (error: unknown) {
        throw toHttpError(error)
      }
    },
    onError: (...args) => {
      options?.onError?.(...args)
    },
    onSuccess: (data, input, ...rest) => {
      const queryKey = credentialsListQueryKey(input.organizationId)

      queryClient.setQueryData<CredentialMeta[]>(queryKey, (credentials) =>
        patchCredentialInList(credentials, input.credentialId, {
          notes: input.payload.notes,
          username: input.payload.username,
          website: input.payload.website,
        })
      )
      void queryClient.invalidateQueries({ queryKey })

      options?.onSuccess?.(data, input, ...rest)
    },
  })

  return useRenameQueryResult(mutation, ApiQueryName.UpdateCredential)
}
